import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Spinner, Surface, PageHeader, EmptyState } from "@/components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Mail, Search, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Kpi, fmtNum, fmtDate, PLAN_LABELS } from "./parts";

const SOURCE = { signup: "Ved oprettelse", checkout: "Ved køb", dashboard: "Dashboard" };

const originOf = (a = {}) => {
  if (a.utm_source) return { main: [a.utm_source, a.utm_medium].filter(Boolean).join(" / "), sub: a.utm_campaign || "" };
  let host = "";
  try { host = a.referrer ? new URL(a.referrer).hostname.replace(/^www\./, "") : ""; } catch { host = a.referrer || ""; }
  if (host && !/aicom\.dk$/.test(host)) return { main: host, sub: "Henvisning" };
  return { main: "Direkte", sub: "" };
};

const toCsv = (rows) => {
  const head = ["E-mail", "Navn", "Virksomhed", "Plan", "Tilmeldt", "Samtykke givet", "Kom fra", "Kampagne", "Henvisende side", "Første side", "Sidst sendt", "Sidst åbnet", "Sidst klikket"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [r.email, r.name, r.company, PLAN_LABELS[r.plan] || r.plan, r.consent_at, SOURCE[r.source] || r.source, originOf(r.attribution).main, r.attribution?.utm_campaign, r.attribution?.referrer, r.attribution?.landing_page, r.last_sent_at, r.last_opened_at, r.last_clicked_at].map(esc).join(";"));
  const blob = new Blob(["\ufeff" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "mandagsmail-tilmeldte.csv";
  a.click();
};

function SubscriberTable({ rows }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => [r.email, r.name, r.company].some((v) => (v || "").toLowerCase().includes(s))) : rows;
  }, [rows, q]);
  return (
    <Surface className="p-0 overflow-hidden mt-8" testid="admin-idea-mail-subscribers">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <p className="font-medium">Tilmeldte <span className="text-muted-foreground font-normal">({rows.length})</span></p>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg e-mail, navn, virksomhed" className="pl-9 w-64" data-testid="admin-idea-mail-search" />
          </div>
          <Button variant="outline" onClick={() => toCsv(list)} disabled={!list.length} className="gap-2" data-testid="admin-idea-mail-export">
            <Download className="h-4 w-4" /> CSV
          </Button>
        </div>
      </div>
      {list.length === 0 ? (
        <EmptyState icon={Mail} title="Ingen tilmeldte" subtitle={q ? "Ingen matcher søgningen." : "Ingen har tilmeldt sig endnu."} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>E-mail</TableHead>
              <TableHead>Navn / virksomhed</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Tilmeldt</TableHead>
              <TableHead>Kom fra</TableHead>
              <TableHead>Sidst åbnet</TableHead>
              <TableHead>Sidst klikket</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((r) => (
              <TableRow key={r.email} data-testid={`admin-idea-mail-sub-${r.email}`}>
                <TableCell className="font-medium">{r.email}</TableCell>
                <TableCell><div>{r.name || "—"}</div><div className="text-xs text-muted-foreground">{r.company || ""}</div></TableCell>
                <TableCell><Badge variant="outline">{PLAN_LABELS[r.plan] || r.plan}</Badge></TableCell>
                <TableCell><div>{r.consent_at ? fmtDate(r.consent_at) : "—"}</div><div className="text-xs text-muted-foreground">{SOURCE[r.source] || ""}</div></TableCell>
                <TableCell data-testid={`admin-idea-mail-origin-${r.email}`} title={r.attribution?.landing_page ? `Første side: ${r.attribution.landing_page}` : ""}>
                  <div>{originOf(r.attribution).main}</div>
                  <div className="text-xs text-muted-foreground">{[originOf(r.attribution).sub, r.attribution?.landing_page].filter(Boolean).join(" · ")}</div>
                </TableCell>
                <TableCell>{r.last_opened_at ? fmtDate(r.last_opened_at) : "—"}</TableCell>
                <TableCell>{r.last_clicked_at ? fmtDate(r.last_clicked_at) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Surface>
  );
}

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)} %` : "—");

export default function AdminIdeaMail() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/admin/idea-mail/stats").then(({ data }) => setData(data)).catch((e) => setErr(apiError(e))); }, []);

  if (err) return <p className="text-destructive text-sm">{err}</p>;
  if (!data) return <Spinner />;
  const t = data.totals;

  return (
    <div data-testid="admin-idea-mail">
      <PageHeader overline="Mandagsmail" title="Ugens SoMe-idéer" subtitle="Tilmeldinger, åbninger og klik på mandagsmailen." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Kpi label="Tilmeldte" value={data.subscribed} tip="Brugere der har givet samtykke og modtager mailen" />
        <Kpi label="Afmeldte" value={data.unsubscribed} tip="Brugere der har afmeldt sig via linket i mailen" />
        <Kpi label="Sendte mails" value={t.sent} tip="Seneste 12 uger" />
        <Kpi label="Åbningsrate" value={pct(t.opened, t.sent)} tip="Unikke åbninger. Apple Mail kan tælle åbninger automatisk, så tallet er vejledende" />
        <Kpi label='Klik på "Skriv opslaget"' value={pct(t.write_clickers, t.sent)} tip={`${fmtNum(t.write_clicks)} klik i alt fra ${fmtNum(t.write_clickers)} modtagere`} />
      </div>
      <Surface className="p-0 overflow-hidden">
        {data.weeks.length === 0 ? (
          <EmptyState icon={Mail} title="Ingen mails sendt endnu" subtitle="Statistikken vises efter første mandagsudsendelse." />
        ) : (
          <Table data-testid="admin-idea-mail-table">
            <TableHeader>
              <TableRow>
                <TableHead>Uge</TableHead>
                <TableHead className="text-right">Sendt</TableHead>
                <TableHead className="text-right">Åbnet</TableHead>
                <TableHead className="text-right">Klikket (alle links)</TableHead>
                <TableHead className="text-right">"Skriv opslaget"</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.weeks.map((w) => (
                <TableRow key={w.week} data-testid={`admin-idea-mail-week-${w.week}`}>
                  <TableCell className="font-mono text-xs">{w.week}</TableCell>
                  <TableCell className="text-right">{fmtNum(w.sent)}</TableCell>
                  <TableCell className="text-right">{fmtNum(w.opened)} <span className="text-muted-foreground text-xs">({pct(w.opened, w.sent)})</span></TableCell>
                  <TableCell className="text-right">{fmtNum(w.clicked)} <span className="text-muted-foreground text-xs">({pct(w.clicked, w.sent)})</span></TableCell>
                  <TableCell className="text-right">{fmtNum(w.write_clickers)} <span className="text-muted-foreground text-xs">({pct(w.write_clickers, w.sent)} · {fmtNum(w.write_clicks)} klik)</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Surface>
      <SubscriberTable rows={data.subscribers || []} />
    </div>
  );
}
